import {
  Component,
  ViewEncapsulation,
  OnInit,
} from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "../../../lib/api.service";
import { LogService } from "../../../lib/log.service";
import { ModalService } from "../../../lib/modal.service";
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AudioCourse } from "../../../models/course";
import { Workbook } from "exceljs";

interface DailyQuote {
  category_id: any;
  quote_en: any;
  quote_fr: any;
  quote_es: any;
}

@Component({
  selector: 'app-daily-series-create',
  templateUrl: './daily-series-create.component.html',
  styleUrls: ['./daily-series-create.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DailySeriesCreateComponent implements OnInit {
  createDailySeriesForm: FormGroup;
  courseEditForm: FormGroup;
  courseKey: string;
  title: string;
  mode: string;
  course: AudioCourse;
  isLoaded: boolean = false;
  errors:string = "";
  file: File = undefined;
  fileError: string = "";
  arrayBuffer: any;
  dailySeries: DailyQuote[] = [];
  courseDailyId: any;
  groupTitle = [];
  submitButtonPressed: boolean = false;
  categoryList: any;
  constructor( private translate: TranslateService,
    private formBuilder: FormBuilder,
    private api: ApiService,
    private log: LogService,
    private modalService: ModalService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location) {
      this.createCourseEditForm(new AudioCourse({}));
      const paramId = this.route.snapshot.paramMap.get('id');
      if (paramId) {
        this.courseDailyId = +paramId;
        this.title = "Edit this Daily Series";
        this.mode = "update";
      } else {
        this.title = "Create a Daily Series";
        this.mode = "create";
        this.isLoaded = true;
      }
      this.createDailySeriesForm = this.formBuilder.group({
        title: ["", [Validators.required]],
        excelFile: ["", [Validators.required]]
      });
     }

     createCourseEditForm(data: any){
      this.courseEditForm = this.formBuilder.group({
        id: [data.id || null],
        category: [data.categoryid || "", [Validators.required]],
        quotesen: [data.quotesen || "", [Validators.required]],
        quotesfr: [data.quotesfr || "", [Validators.required]],
        quoteses: [data.quoteses || "", [Validators.required]]
      });
    }

     get f() {
      return this.createDailySeriesForm.controls;
    }

    get g() {
      return this.courseEditForm.controls;
    }

  ngOnInit(): void {
    if (this.courseDailyId) {
      this.api
        .get(`admin/series/dailyseries/` + this.courseDailyId)
        .subscribe(
          (result: any) => {
            this.course = new AudioCourse(result[0]);
            this.createCourseEditForm(this.course);
            this.isLoaded = true;
          },
          (error: any) => {
            this.log.error(
              "Error getting organizations. " + error.message
            );
          }
        );
         this.api
        .get("series/categories")
        .subscribe(
          (result: any) => {
            this.categoryList = result;
            this.isLoaded = true;
          },
          (error: any) => {
            this.log.error(
              "Error getting organizations. " + error.message
            );
          }
        );
    }
    if (this.mode === 'create') {
      this.api.get('series/dailyseries/getgroups').subscribe(
        (result: any) => {
          this.groupTitle = result || [];
        },
        (error: any) => {
          this.log.error('Error getting groups. ' + error.message);
        }
      );
    }
  }

  incomingfile(event) {
		this.file = event.target.files[0];
	}

  doSave() {
    if (this.mode === "update") {
      this.course = new AudioCourse(this.courseEditForm.value);
      this.submitButtonPressed = true;
      if (!this.courseEditForm.valid) {
        this.courseEditForm.markAllAsTouched();
        return;
      }
      this.errors = "";
      if (this.errors) {
        return;
      }
      this.isLoaded = false;
      let payload = {
        category: this.courseEditForm.value.category,
        title_en: this.courseEditForm.value.quotesen,
        title_fr: this.courseEditForm.value.quotesfr,
        title_es: this.courseEditForm.value.quoteses,
      }
      this.api
        .post(
          "series/dailyseries/update/" + this.courseDailyId,
          payload
        )
        .subscribe(
          (data: any) => {
            this.modalService.showAlert(
              "Success",
              "Daily Series has been updated"
            );
            this.isLoaded = true;
            this.router.navigate(['/admin/series-listing']);
          },
          (error: any) => {
            this.isLoaded = true;
            this.modalService.showAlert(
              "Error",
              "Something went wrong. " + error.message
            );
          }
        );
    } else {
      this.fileError = "";
      if (!this.file) {
        this.fileError = "Please upload a valid file";
        return;
      }
      this.uploadSpreadsheet();
    }
  }

  uploadSpreadsheet() {
      this.dailySeries = [];
      this.fileError = "";
      let fileReader = new FileReader();
      fileReader.readAsArrayBuffer(this.file);
      fileReader.onload = async () => {
        try {
          this.arrayBuffer = fileReader.result;
          const workbook = new Workbook();
          await workbook.xlsx.load(this.arrayBuffer as ArrayBuffer);
          const worksheet = workbook.worksheets[0];

          const headerRow: string[] = [];
          worksheet.getRow(1).eachCell((cell: any, col: number) => {
            headerRow[col - 1] = String(cell.value ?? "").trim();
          });

          const requiredHeaders = ["Category ID", "English", "French", "Spanish"];
          const missingHeaders = requiredHeaders.filter(h => !headerRow.includes(h));

          if (missingHeaders.length > 0) {
            this.fileError = `Missing required columns / Title of columns: ${missingHeaders.join(", ")}`;
            return;
          }

          const titleToCheck = this.createDailySeriesForm.value.title.trim();
          const alreadyExists = this.groupTitle.some(group => group.Name.trim().toLowerCase() === titleToCheck.toLowerCase());

          if (alreadyExists) {
            this.fileError = "Title already exists.";
            return;
          }

          const importedData: any[] = [];
          for (let r = 2; r <= worksheet.rowCount; r++) {
            const row = worksheet.getRow(r);
            const obj: Record<string, any> = {};
            let hasData = false;
            row.eachCell({ includeEmpty: false }, (cell: any, col: number) => {
              const key = headerRow[col - 1];
              if (!key) return;
              obj[key] = this.unwrapCell(cell);
              hasData = true;
            });
            if (hasData) importedData.push(obj);
          }

          this.log.debug(importedData);
          for (let i = 0; i < importedData.length; i++) {
            const content = {
              category_id: importedData[i]["Category ID"],
              quote_en: importedData[i]["English"],
              quote_fr: importedData[i]["French"],
              quote_es: importedData[i]["Spanish"]
            }
            this.dailySeries.push(content)
          }
          if (this.dailySeries.length === 0) {
            this.fileError = "Invalid file or no items found"!;
            return;
          }
          this.importResources();
        } catch (err: any) {
          this.fileError = `Failed to read Excel file: ${err?.message || err}`;
          this.log.error("Excel parse error: " + (err?.message || err));
        }
      };
    }

    private unwrapCell(cell: any): any {
      const v = cell.value;
      if (v === null || v === undefined) return "";
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
      if (v instanceof Date) return v;
      if (typeof v === "object" && v !== null && "richText" in v) {
        return (v as any).richText.map((r: any) => r.text).join("");
      }
      if (typeof v === "object" && v !== null && "result" in v) return (v as any).result;
      if (typeof v === "object" && v !== null && "text" in v) return (v as any).text;
      return v;
    }

    importResources() {
        this.isLoaded = false;
        let payload = {
          title: this.createDailySeriesForm.value.title,
          daily_series: this.dailySeries,
        }
        this.api
          .post(
            `series/dailyseries/import`,payload
          )
          .subscribe(
            (data: any) => {
              this.modalService.showAlert(
                "Success",
                "Daily Series has been imported"
              );
              this.isLoaded = true;
              this.router.navigate(['/admin/series-listing']);
            },
            (error: any) => {
              const rowErrors: string[] = error?.error?.errors || error?.errors || [];
              const messageParts: string[] = [];
              if (error?.error?.message || error?.message) {
                messageParts.push(error?.error?.message || error.message);
              }
              if (rowErrors.length > 0) {
                messageParts.push(rowErrors.join('\n'));
              }
              const message = messageParts.length
                ? messageParts.join('\n\n')
                : 'Something went wrong importing the daily series.';
              this.modalService.showAlert("Import failed", message);
              delete this.file;
              this.isLoaded = true;
            }
          );
      }

  onClose() {
    this.location.back();
  }
}
